import React, { useEffect } from 'react';
import './MobileAlert.css';

const SuccessIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 7c-1.6-2.4-4.3-4-7.4-4C7.7 3 4 6.7 4 11.6 4 16.6 8 21 12.9 21c3.6 0 6.6-2.5 7.5-5.8" />
    <path d="m9 11 3 3L22 4" />
  </svg>
);

const WarningIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.3 3.5 1.8 18.3c-.9 1.6.2 3.7 2.1 3.7h16.2c1.9 0 3-2.1 2.1-3.7L13.7 3.5c-.9-1.7-3.3-1.7-4.4 0Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);

const ErrorIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 7v5" />
    <path d="M12 16h.01" />
  </svg>
);

const InfoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 3l8 8" />
    <path d="M11 3 3 11" />
  </svg>
);

const VARIANT_CONFIG = {
  success: { Icon: SuccessIcon, accent: '#34d399' },
  warning: { Icon: WarningIcon, accent: '#fbbf24' },
  error: { Icon: ErrorIcon, accent: '#f87171' },
  info: { Icon: InfoIcon, accent: '#60a5fa' }
};

const ensureConfig = (variant) => VARIANT_CONFIG[variant] || VARIANT_CONFIG.info;

const MobileAlertStack = ({ alerts, onDismiss }) => {
  if (!alerts || alerts.length === 0) {
    return null;
  }

  return (
    <div className="mobile-alert-stack" role="region" aria-live="polite" aria-label="Notifiche">
      {alerts.map((alert) => (
        <AlertItem key={alert.id} alert={alert} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

const AlertItem = ({ alert, onDismiss }) => {
  const { Icon, accent } = ensureConfig(alert.variant);

  useEffect(() => {
    if (!alert.duration) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      onDismiss(alert.id);
    }, alert.duration);

    return () => window.clearTimeout(timer);
  }, [alert.duration, alert.id, onDismiss]);

  const handleActionClick = (action) => {
    try {
      if (typeof action?.onClick === 'function') {
        action.onClick();
      }
    } finally {
      if (action?.dismissOnClick !== false) {
        onDismiss(alert.id);
      }
    }
  };

  return (
    <div className={`mobile-alert mobile-alert--${alert.variant}`} role="alert">
      <div className="mobile-alert__body">
        <div className="mobile-alert__icon-wrapper" style={{ color: accent }}>
          <Icon />
        </div>
        <div className="mobile-alert__content">
          {alert.title && <p className="mobile-alert__title">{alert.title}</p>}
          {alert.description && <p className="mobile-alert__description">{alert.description}</p>}
          {alert.actions && alert.actions.length > 0 && (
            <div className="mobile-alert__actions">
              {alert.actions.map((action) => (
                <button
                  key={action.id}
                  className={`mobile-alert__button mobile-alert__button--${action.variant || 'secondary'}`}
                  onClick={() => handleActionClick(action)}
                  type="button"
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {alert.dismissible !== false && (
          <button
            type="button"
            className="mobile-alert__close"
            onClick={() => onDismiss(alert.id)}
            aria-label="Chiudi notifica"
          >
            <CloseIcon />
          </button>
        )}
      </div>
    </div>
  );
};

export default MobileAlertStack;
